import React, { useEffect, lazy, Suspense } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { Box, CircularProgress } from '@mui/material';
import { lightTheme } from './theme/muiTheme';
import './App.css';
import { ADMIN_ROUTE } from './constants/routes';
const AdminRoute = lazy(() => import('./components/AdminRoute'));
import { initializeUserPreferences } from './services/userPreferencesService';
import Toast from './components/Toast';
import RGPDBanner from './components/RGPDBanner';
import ErrorBoundary from './components/ErrorBoundary';
import { startAutoBackup } from './services/backupService';
import { trackPageView } from './services/trackingService';

// Composants layout (chargés immédiatement — page d'accueil)
import HeroHeader from './components/layout/HeroHeader';
import MainGallerySection from './components/layout/MainGallerySection';
import StickyNav from './components/layout/StickyNav';
import Footer from './components/Footer';

// Routes secondaires — lazy loading (code-splitting)
const LegalPages = lazy(() => import('./components/LegalPages'));
const PolitiqueConfidentialite = lazy(() => import('./components/PolitiqueConfidentialite'));
const MentionsLegales = lazy(() => import('./components/MentionsLegales'));
const NotFound404 = lazy(() => import('./components/NotFound404'));
const ContactArtist = lazy(() => import('./components/ContactArtist'));
const Biographie = lazy(() => import('./components/Biographie'));
const TableauPage = lazy(() => import('./components/TableauPage'));

// Stores Zustand
import { useUIStore } from './stores/useGalleryStore';

// Hooks React Query
import { useCategories } from './hooks/useTableauxQueries';

// Hooks de protection et RGPD
import { useGlobalProtection, useImageProtection } from './hooks/useImageProtection';

function App() {
  // Zustand stores pour l'UI
  const {
    showHeader,
    selectedTableau,
    setShowHeader,
    selectTableau,
    closeTableau,
  } = useUIStore();

  const { isLoading: categoriesLoading } = useCategories();

  // Thème forcé clair
  const currentTheme = lightTheme;

  // Initialiser les protections globales et la protection d'images
  useGlobalProtection();
  useImageProtection({
    watermark: true,
    protection: 'medium',
    dynamicWatermark: true,
    autoApply: true,
  });

  // Initialisation au chargement
  useEffect(() => {
    const initialize = async () => {
      // Initialiser les préférences utilisateur (applique aussi le thème et les animations)
      initializeUserPreferences();

      // Démarrer la sauvegarde automatique
      startAutoBackup();
    };

    initialize();
  }, []);

  // Handlers simplifiés
  const navigate = useNavigate();
  const location = useLocation();

  // Tracking page view on route change
  useEffect(() => {
    trackPageView(location.pathname, document.title);
  }, [location.pathname]);

  // Gestion du scroll optimisée
  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          setShowHeader(window.pageYOffset < 50);
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [setShowHeader]);
  const isAdminRoute = location.pathname.startsWith(`/${ADMIN_ROUTE}`) || location.pathname.startsWith('/admins');
  const isHomePage = location.pathname === '/';
  // Sur la home : nav visible quand scrollé (hero hors vue). Sur les autres pages : toujours visible.
  const navVisible = !isAdminRoute && (!isHomePage || !showHeader);

  const handleContact = () => {
    navigate('/contact-artist');
  };
  const handleBio = () => {
    navigate('/biographie');
  };

  const scrollToGallery = () => {
    const element = document.querySelector('.main-content');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const isLoading = categoriesLoading;

  return (
    <ThemeProvider theme={currentTheme}>
      <CssBaseline />
      <Box className="app-container">
        <ErrorBoundary>
        {navVisible && <StickyNav />}
        <Toast />
        <RGPDBanner />
          <Suspense fallback={
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
              <CircularProgress />
            </Box>
          }>
            {/* Décalage pour la navbar fixe (52px) sur toutes les pages sauf home et admin */}
            <Box sx={{ pt: navVisible && !isHomePage ? '52px' : 0 }}>
            <Routes>
              <Route
                path="/"
                element={
                  <>
                    <HeroHeader
                      isDarkMode={false}
                      onScrollToGallery={scrollToGallery}
                      onContact={handleContact}
                      onBio={handleBio}
                    />
                    <MainGallerySection showHeader={showHeader} />
                  </>
                }
              />
              <Route path={ADMIN_ROUTE} element={<AdminRoute />} />
              {/* Alias court pour l'accès admin — même composant */}
              <Route path="admins" element={<AdminRoute />} />
              <Route path="/legal/*" element={<LegalPages />} />
              <Route path="/politique-confidentialite" element={<PolitiqueConfidentialite />} />
              <Route path="/mentions-legales" element={<MentionsLegales />} />
              <Route path="/tableau/:id" element={
                <ErrorBoundary>
                  <TableauPage
                    showHeader={showHeader}
                    onScrollToGallery={scrollToGallery}
                    onContact={handleContact}
                    onBio={handleBio}
                  />
                </ErrorBoundary>
              } />
              <Route path="/biographie" element={<Biographie />} />
              <Route path="/contact-artist" element={<ContactArtist />} />
              <Route path="*" element={<NotFound404 />} />
            </Routes>
            </Box>
          </Suspense>
        </ErrorBoundary>
        {!isAdminRoute && <Footer />}
      </Box>
    </ThemeProvider>
  );
}

export default App;
